import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';

// Maps currency codes to the flag emoji of their primary associated country/region.
// Covers all codes that any consumer currently uses; extend as new codes are adopted.
const CURRENCY_FLAG_MAP: Readonly<Record<string, string>> = {
  AED: '🇦🇪',
  AFN: '🇦🇫',
  ALL: '🇦🇱',
  AMD: '🇦🇲',
  ANG: '🇳🇱',
  AOA: '🇦🇴',
  ARS: '🇦🇷',
  AUD: '🇦🇺',
  AWG: '🇦🇼',
  AZN: '🇦🇿',
  BAM: '🇧🇦',
  BBD: '🇧🇧',
  BDT: '🇧🇩',
  BGN: '🇧🇬',
  BHD: '🇧🇭',
  BIF: '🇧🇮',
  BMD: '🇧🇲',
  BND: '🇧🇳',
  BOB: '🇧🇴',
  BRL: '🇧🇷',
  BSD: '🇧🇸',
  BTN: '🇧🇹',
  BWP: '🇧🇼',
  BYN: '🇧🇾',
  BZD: '🇧🇿',
  CAD: '🇨🇦',
  CDF: '🇨🇩',
  CHF: '🇨🇭',
  CLP: '🇨🇱',
  CNY: '🇨🇳',
  COP: '🇨🇴',
  CRC: '🇨🇷',
  CUP: '🇨🇺',
  CVE: '🇨🇻',
  CZK: '🇨🇿',
  DJF: '🇩🇯',
  DKK: '🇩🇰',
  DOP: '🇩🇴',
  DZD: '🇩🇿',
  EGP: '🇪🇬',
  ERN: '🇪🇷',
  ETB: '🇪🇹',
  EUR: '🇪🇺',
  FJD: '🇫🇯',
  FKP: '🇫🇰',
  GBP: '🇬🇧',
  GEL: '🇬🇪',
  GHS: '🇬🇭',
  GIP: '🇬🇮',
  GMD: '🇬🇲',
  GNF: '🇬🇳',
  GTQ: '🇬🇹',
  GYD: '🇬🇾',
  HKD: '🇭🇰',
  HNL: '🇭🇳',
  HRK: '🇭🇷',
  HTG: '🇭🇹',
  HUF: '🇭🇺',
  IDR: '🇮🇩',
  ILS: '🇮🇱',
  INR: '🇮🇳',
  IQD: '🇮🇶',
  IRR: '🇮🇷',
  ISK: '🇮🇸',
  JMD: '🇯🇲',
  JOD: '🇯🇴',
  JPY: '🇯🇵',
  KES: '🇰🇪',
  KGS: '🇰🇬',
  KHR: '🇰🇭',
  KMF: '🇰🇲',
  KPW: '🇰🇵',
  KRW: '🇰🇷',
  KWD: '🇰🇼',
  KYD: '🇰🇾',
  KZT: '🇰🇿',
  LAK: '🇱🇦',
  LBP: '🇱🇧',
  LKR: '🇱🇰',
  LRD: '🇱🇷',
  LSL: '🇱🇸',
  LYD: '🇱🇾',
  MAD: '🇲🇦',
  MDL: '🇲🇩',
  MGA: '🇲🇬',
  MKD: '🇲🇰',
  MMK: '🇲🇲',
  MNT: '🇲🇳',
  MOP: '🇲🇴',
  MRU: '🇲🇷',
  MUR: '🇲🇺',
  MVR: '🇲🇻',
  MWK: '🇲🇼',
  MXN: '🇲🇽',
  MYR: '🇲🇾',
  MZN: '🇲🇿',
  NAD: '🇳🇦',
  NGN: '🇳🇬',
  NIO: '🇳🇮',
  NOK: '🇳🇴',
  NPR: '🇳🇵',
  NZD: '🇳🇿',
  OMR: '🇴🇲',
  PAB: '🇵🇦',
  PEN: '🇵🇪',
  PGK: '🇵🇬',
  PHP: '🇵🇭',
  PKR: '🇵🇰',
  PLN: '🇵🇱',
  PYG: '🇵🇾',
  QAR: '🇶🇦',
  RON: '🇷🇴',
  RSD: '🇷🇸',
  RUB: '🇷🇺',
  RWF: '🇷🇼',
  SAR: '🇸🇦',
  SBD: '🇸🇧',
  SCR: '🇸🇨',
  SDG: '🇸🇩',
  SEK: '🇸🇪',
  SGD: '🇸🇬',
  SHP: '🇸🇭',
  SLE: '🇸🇱',
  SOS: '🇸🇴',
  SRD: '🇸🇷',
  SSP: '🇸🇸',
  STN: '🇸🇹',
  SVC: '🇸🇻',
  SYP: '🇸🇾',
  SZL: '🇸🇿',
  THB: '🇹🇭',
  TJS: '🇹🇯',
  TMT: '🇹🇲',
  TND: '🇹🇳',
  TOP: '🇹🇴',
  TRY: '🇹🇷',
  TTD: '🇹🇹',
  TWD: '🇹🇼',
  TZS: '🇹🇿',
  UAH: '🇺🇦',
  UGX: '🇺🇬',
  USD: '🇺🇸',
  UYU: '🇺🇾',
  UZS: '🇺🇿',
  VES: '🇻🇪',
  VND: '🇻🇳',
  VUV: '🇻🇺',
  WST: '🇼🇸',
  XAF: '🇨🇲',
  XCD: '🇦🇬',
  XOF: '🇸🇳',
  XPF: '🇵🇫',
  YER: '🇾🇪',
  ZAR: '🇿🇦',
  ZMW: '🇿🇲',
  ZWL: '🇿🇼',
};

export const getCurrencyFlag = (code: CurrencyCodeType): string => {
  return CURRENCY_FLAG_MAP[code] ?? '';
};

// Translatable currency names keyed by code.
// The hook returns a lookup so consumers can resolve names for any code set.
export const useCurrencyName = (): ((code: CurrencyCodeType) => string) => {
  const { tq } = useTranslation('currencies');

  return useMemo(() => {
    const nameMap: Record<string, string> = {
      AED: tq`UAE Dirham`,
      AFN: tq`Afghan Afghani`,
      ALL: tq`Albanian Lek`,
      AMD: tq`Armenian Dram`,
      ANG: tq`Netherlands Antillean Guilder`,
      AOA: tq`Angolan Kwanza`,
      ARS: tq`Argentine Peso`,
      AUD: tq`Australian Dollar`,
      AWG: tq`Aruban Florin`,
      AZN: tq`Azerbaijani Manat`,
      BAM: tq`Bosnia-Herzegovina Convertible Mark`,
      BBD: tq`Barbadian Dollar`,
      BDT: tq`Bangladeshi Taka`,
      BGN: tq`Bulgarian Lev`,
      BHD: tq`Bahraini Dinar`,
      BIF: tq`Burundian Franc`,
      BMD: tq`Bermudan Dollar`,
      BND: tq`Brunei Dollar`,
      BOB: tq`Bolivian Boliviano`,
      BRL: tq`Brazilian Real`,
      BSD: tq`Bahamian Dollar`,
      BTN: tq`Bhutanese Ngultrum`,
      BWP: tq`Botswanan Pula`,
      BYN: tq`Belarusian Ruble`,
      BZD: tq`Belize Dollar`,
      CAD: tq`Canadian Dollar`,
      CDF: tq`Congolese Franc`,
      CHF: tq`Swiss Franc`,
      CLP: tq`Chilean Peso`,
      CNY: tq`Chinese Yuan`,
      COP: tq`Colombian Peso`,
      CRC: tq`Costa Rican Colón`,
      CUP: tq`Cuban Peso`,
      CVE: tq`Cape Verdean Escudo`,
      CZK: tq`Czech Koruna`,
      DJF: tq`Djiboutian Franc`,
      DKK: tq`Danish Krone`,
      DOP: tq`Dominican Peso`,
      DZD: tq`Algerian Dinar`,
      EGP: tq`Egyptian Pound`,
      ERN: tq`Eritrean Nakfa`,
      ETB: tq`Ethiopian Birr`,
      EUR: tq`Euro`,
      FJD: tq`Fijian Dollar`,
      FKP: tq`Falkland Islands Pound`,
      GBP: tq`Pound Sterling`,
      GEL: tq`Georgian Lari`,
      GHS: tq`Ghanaian Cedi`,
      GIP: tq`Gibraltar Pound`,
      GMD: tq`Gambian Dalasi`,
      GNF: tq`Guinean Franc`,
      GTQ: tq`Guatemalan Quetzal`,
      GYD: tq`Guyanaese Dollar`,
      HKD: tq`Hong Kong Dollar`,
      HNL: tq`Honduran Lempira`,
      HRK: tq`Croatian Kuna`,
      HTG: tq`Haitian Gourde`,
      HUF: tq`Hungarian Forint`,
      IDR: tq`Indonesian Rupiah`,
      ILS: tq`Israeli New Shekel`,
      INR: tq`Indian Rupee`,
      IQD: tq`Iraqi Dinar`,
      IRR: tq`Iranian Rial`,
      ISK: tq`Icelandic Króna`,
      JMD: tq`Jamaican Dollar`,
      JOD: tq`Jordanian Dinar`,
      JPY: tq`Japanese Yen`,
      KES: tq`Kenyan Shilling`,
      KGS: tq`Kyrgystani Som`,
      KHR: tq`Cambodian Riel`,
      KMF: tq`Comorian Franc`,
      KPW: tq`North Korean Won`,
      KRW: tq`South Korean Won`,
      KWD: tq`Kuwaiti Dinar`,
      KYD: tq`Cayman Islands Dollar`,
      KZT: tq`Kazakhstani Tenge`,
      LAK: tq`Laotian Kip`,
      LBP: tq`Lebanese Pound`,
      LKR: tq`Sri Lankan Rupee`,
      LRD: tq`Liberian Dollar`,
      LSL: tq`Lesotho Loti`,
      LYD: tq`Libyan Dinar`,
      MAD: tq`Moroccan Dirham`,
      MDL: tq`Moldovan Leu`,
      MGA: tq`Malagasy Ariary`,
      MKD: tq`Macedonian Denar`,
      MMK: tq`Myanma Kyat`,
      MNT: tq`Mongolian Tugrik`,
      MOP: tq`Macanese Pataca`,
      MRU: tq`Mauritanian Ouguiya`,
      MUR: tq`Mauritian Rupee`,
      MVR: tq`Maldivian Rufiyaa`,
      MWK: tq`Malawian Kwacha`,
      MXN: tq`Mexican Peso`,
      MYR: tq`Malaysian Ringgit`,
      MZN: tq`Mozambican Metical`,
      NAD: tq`Namibian Dollar`,
      NGN: tq`Nigerian Naira`,
      NIO: tq`Nicaraguan Córdoba`,
      NOK: tq`Norwegian Krone`,
      NPR: tq`Nepalese Rupee`,
      NZD: tq`New Zealand Dollar`,
      OMR: tq`Omani Rial`,
      PAB: tq`Panamanian Balboa`,
      PEN: tq`Peruvian Sol`,
      PGK: tq`Papua New Guinean Kina`,
      PHP: tq`Philippine Peso`,
      PKR: tq`Pakistani Rupee`,
      PLN: tq`Polish Zloty`,
      PYG: tq`Paraguayan Guarani`,
      QAR: tq`Qatari Riyal`,
      RON: tq`Romanian Leu`,
      RSD: tq`Serbian Dinar`,
      RUB: tq`Russian Ruble`,
      RWF: tq`Rwandan Franc`,
      SAR: tq`Saudi Riyal`,
      SBD: tq`Solomon Islands Dollar`,
      SCR: tq`Seychellois Rupee`,
      SDG: tq`Sudanese Pound`,
      SEK: tq`Swedish Krona`,
      SGD: tq`Singapore Dollar`,
      SHP: tq`Saint Helena Pound`,
      SLE: tq`Sierra Leonean Leone`,
      SOS: tq`Somali Shilling`,
      SRD: tq`Surinamese Dollar`,
      SSP: tq`South Sudanese Pound`,
      STN: tq`São Tomé and Príncipe Dobra`,
      SVC: tq`Salvadoran Colón`,
      SYP: tq`Syrian Pound`,
      SZL: tq`Swazi Lilangeni`,
      THB: tq`Thai Baht`,
      TJS: tq`Tajikistani Somoni`,
      TMT: tq`Turkmenistani Manat`,
      TND: tq`Tunisian Dinar`,
      TOP: tq`Tongan Paʻanga`,
      TRY: tq`Turkish Lira`,
      TTD: tq`Trinidad and Tobago Dollar`,
      TWD: tq`New Taiwan Dollar`,
      TZS: tq`Tanzanian Shilling`,
      UAH: tq`Ukrainian Hryvnia`,
      UGX: tq`Ugandan Shilling`,
      USD: tq`United States Dollar`,
      UYU: tq`Uruguayan Peso`,
      UZS: tq`Uzbekistan Som`,
      VES: tq`Venezuelan Bolívar`,
      VND: tq`Vietnamese Dong`,
      VUV: tq`Vanuatu Vatu`,
      WST: tq`Samoan Tala`,
      XAF: tq`Central African CFA Franc`,
      XCD: tq`East Caribbean Dollar`,
      XOF: tq`West African CFA Franc`,
      XPF: tq`CFP Franc`,
      YER: tq`Yemeni Rial`,
      ZAR: tq`South African Rand`,
      ZMW: tq`Zambian Kwacha`,
      ZWL: tq`Zimbabwean Dollar`,
    };

    return (code: CurrencyCodeType): string => nameMap[code] ?? code;
  }, [tq]);
};

export type CurrencyMetadata = Readonly<{
  code: CurrencyCodeType;
  name: string;
  flag: string;
}>;

// Resolves a list of currency codes into full metadata (name + flag).
export const useCurrencyMetadata = (codes: ReadonlyArray<CurrencyCodeType>): ReadonlyArray<CurrencyMetadata> => {
  const getName = useCurrencyName();

  return useMemo(
    () =>
      codes.map(code => ({
        code,
        name: getName(code),
        flag: getCurrencyFlag(code),
      })),
    [codes, getName],
  );
};
